/* eslint-disable @typescript-eslint/no-explicit-any */
import { oc } from 'ts-optchain';
import _ from 'lodash';

import { core, safeEnumCompare } from '@tock/coretypes';
import AppSettings from 'settings/AppSettings';
import protos from 'utils/protos';

const { PaymentProcessorType } = protos;

const setPublishableKey = _.once(() => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  window.stripeV3 = window.Stripe!(AppSettings.STRIPE_PUBLIC_KEY);
});

export function isStripeProcessorType(processorType?: core.PaymentProcessorType): boolean {
  return safeEnumCompare(processorType, PaymentProcessorType, PaymentProcessorType.STRIPE);
}

export function hasStripeTransaction(changeHistory: core.PurchaseChangeHistory[]): boolean {
  return changeHistory.some((history) => isStripeProcessorType(history.processorType));
}

/**
 * Initialized Stripe with the configured publishable key.
 */
export function initializeStripe() {
  if (window.Stripe) {
    setPublishableKey();
  }
}

/**
 * Generates the link to Stripe given details about the purchase transaction.
 * @param {object} business the current business being displayed
 * @param {array} changeHistory all of the changes made on a purchase
 * @param {string} confirmationId the confirmation id of the pruchase
 * @returns {string} the URL to the Stripe transaction details, or undefined if it can't be computed
 */
export function getStripeTransactionUrl(
  changeHistory: core.IPurchaseChangeHistory[],
  confirmationId: string,
  businessId: number
): string | undefined {
  const stripeTransaction = _.find(changeHistory, (history: core.PurchaseChangeHistory) => {
    return (
      history.referenceId &&
      history.referenceId.toLowerCase().endsWith(confirmationId.toLowerCase()) &&
      history.amountPaidCents > 0
    );
  });
  if (!stripeTransaction) {
    return undefined;
  }

  const purchaseId = oc(stripeTransaction as any).purchaseId();
  return `${AppSettings.API_BASE}/payment/link?purchaseId=${purchaseId}&businessId=${businessId}`;
}

/**
 * Returns a Stripe object that can make purchase calls on behalf of a connected business.
 */
export function getStripeObjectForBusiness(stripeAccount: string) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return window.Stripe!(AppSettings.STRIPE_PUBLIC_KEY, { stripeAccount });
}

/**
 * Returns the link to use to connect to a Stripe account.
 */
export function getStripeConnectUrl(businessId: number, redirectUrl?: string) {
  const optionalRedirectUrl = redirectUrl ? `&redirect_uri=${redirectUrl}` : '';
  return `https://connect.stripe.com/oauth/authorize?response_type=code&scope=read_write&client_id=${AppSettings.STRIPE_CONNECT_KEY}&state=${businessId}${optionalRedirectUrl}`;
}
